import { computed, ref, type Ref } from 'vue'
import { createQuery } from '@/queries'
import {
  getCategoryTreeV1,
  type CategoryTreeAudience,
  type CategoryTreeLang,
  type CategoryTreeResponseV1,
} from '@/features/search/api/categories'

/** Every tab the burger menu offers — loaded together at app start (2026-10-22). */
export const CATEGORY_TREE_AUDIENCES: readonly CategoryTreeAudience[] = ['woman', 'man', 'kids']

/**
 * The burger menu's tree for one tab in one language. Keyed on both, so
 * switching Women → Men refetches (the counts differ per tab) and the entry
 * of each tab stays cached for the session.
 */
export const useCategoryTreeV1Query = (
  audience: Ref<CategoryTreeAudience>,
  lang: Ref<CategoryTreeLang>,
) => {
  return createQuery<CategoryTreeResponseV1>({
    key: computed(() => ['categories', 'tree', 'v1', audience.value, lang.value]),
    fetcher: () => getCategoryTreeV1(audience.value, lang.value),
    // Once per session (2026-10-22): the tree changes with the nightly refill or an admin edit; the
    // API answers it from a precomputed copy with `max-age=3600, stale-while-revalidate=86400`.
    staleTime: Number.POSITIVE_INFINITY,
    enabled: computed(() => true),
  })
}

/**
 * App start: all three tabs of the current language, so switching Women → Men → Kids in the menu
 * reads the cache instead of showing skeletons. Mounted once (AppLayout); the subscriptions keep
 * the entries alive for the session.
 */
export const usePrefetchCategoryTreesV1 = (lang: Ref<CategoryTreeLang>) =>
  CATEGORY_TREE_AUDIENCES.map((audience) => useCategoryTreeV1Query(ref(audience), lang))
